import { supabase } from './supabase';

/**
 * Paid tier catalog for CTO as a Service — the rows behind
 * members.stephansmith.me/upgrade. Mirrors members/src/lib/plans.ts, which is still
 * a hardcoded stub; this table is meant to replace it.
 *
 * See supabase/migrations/20260901140000_membership_tiers.sql.
 */

export const MEMBERSHIP_AUDIENCES = ['individual', 'group', 'gift'] as const;
export type MembershipAudience = (typeof MEMBERSHIP_AUDIENCES)[number];

export interface MembershipTierRow {
  id: string;
  slug: string;
  audience: MembershipAudience;
  name: string;
  priceLabel: string;
  priceHint: string | null;
  stripePriceId: string | null;
  /** Sandbox counterpart, read by deployments running with STRIPE_LIVEMODE false. */
  stripePriceIdTest: string | null;
  customAmountMin: number | null;
  customAmountDefault: number | null;
  customAmountSuffix: string | null;
  isFree: boolean;
  featured: boolean;
  active: boolean;
  sortOrder: number;
  /** null = unlimited at the catalog level. */
  monthlyCallLimit: number | null;
}

export interface MembershipFeatureRow {
  id: string;
  slug: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

/**
 * A benefit is either absent from a tier's card, listed as included, or listed
 * struck through. The free tier depends on the third state to sell the upgrade.
 */
export type TierFeatureState = 'hidden' | 'included' | 'excluded';

export interface MembershipCatalog {
  tiers: MembershipTierRow[];
  features: MembershipFeatureRow[];
  /** Keyed `${tierId}:${featureId}` — absent means 'hidden'. */
  states: Record<string, TierFeatureState>;
}

export function audienceLabel(audience: MembershipAudience): string {
  if (audience === 'group') return 'Group';
  if (audience === 'gift') return 'Gift';
  return 'Individual';
}

export function tierFeatureKey(tierId: string, featureId: string): string {
  return `${tierId}:${featureId}`;
}

/** numeric columns can arrive as strings over PostgREST depending on magnitude. */
function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function mapTierRow(raw: Record<string, unknown>): MembershipTierRow {
  const audience = String(raw.audience ?? 'individual');
  return {
    id: String(raw.id),
    slug: String(raw.slug ?? ''),
    audience: (MEMBERSHIP_AUDIENCES as readonly string[]).includes(audience)
      ? (audience as MembershipAudience)
      : 'individual',
    name: String(raw.name ?? ''),
    priceLabel: String(raw.price_label ?? ''),
    priceHint: str(raw.price_hint),
    stripePriceId: str(raw.stripe_price_id),
    stripePriceIdTest: str(raw.stripe_price_id_test),
    customAmountMin: num(raw.custom_amount_min),
    customAmountDefault: num(raw.custom_amount_default),
    customAmountSuffix: str(raw.custom_amount_suffix),
    isFree: Boolean(raw.is_free),
    featured: Boolean(raw.featured),
    active: Boolean(raw.active),
    sortOrder: num(raw.sort_order) ?? 0,
    monthlyCallLimit: num(raw.monthly_call_limit),
  };
}

function mapFeatureRow(raw: Record<string, unknown>): MembershipFeatureRow {
  return {
    id: String(raw.id),
    slug: String(raw.slug ?? ''),
    label: String(raw.label ?? ''),
    sortOrder: num(raw.sort_order) ?? 0,
    active: Boolean(raw.active),
  };
}

export async function fetchMembershipCatalog(): Promise<MembershipCatalog> {
  const [tiersRes, featuresRes, linkRes] = await Promise.all([
    supabase
      .from('membership_tiers')
      .select('*')
      .order('audience', { ascending: true })
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true }),
    supabase
      .from('membership_features')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('label', { ascending: true }),
    supabase.from('membership_tier_features').select('tier_id, feature_id, included'),
  ]);

  if (tiersRes.error) throw new Error(tiersRes.error.message);
  if (featuresRes.error) throw new Error(featuresRes.error.message);
  if (linkRes.error) throw new Error(linkRes.error.message);

  const states: Record<string, TierFeatureState> = {};
  for (const raw of linkRes.data ?? []) {
    const row = raw as Record<string, unknown>;
    states[tierFeatureKey(String(row.tier_id), String(row.feature_id))] = row.included
      ? 'included'
      : 'excluded';
  }

  return {
    tiers: (tiersRes.data ?? []).map((r) => mapTierRow(r as Record<string, unknown>)),
    features: (featuresRes.data ?? []).map((r) => mapFeatureRow(r as Record<string, unknown>)),
    states,
  };
}

/** Fields a tier form can write. `slug` is immutable once saved — app code keys off it. */
export interface MembershipTierInput {
  slug: string;
  audience: MembershipAudience;
  name: string;
  priceLabel: string;
  priceHint: string | null;
  stripePriceId: string | null;
  stripePriceIdTest: string | null;
  customAmountMin: number | null;
  customAmountDefault: number | null;
  customAmountSuffix: string | null;
  isFree: boolean;
  featured: boolean;
  active: boolean;
  sortOrder: number;
  monthlyCallLimit: number | null;
}

function toTierPayload(input: MembershipTierInput): Record<string, unknown> {
  // The DB rejects a free tier carrying a price; normalise here so the constraint is
  // a backstop rather than the error the admin sees.
  const paid = !input.isFree;
  return {
    slug: input.slug,
    audience: input.audience,
    name: input.name,
    price_label: input.priceLabel,
    price_hint: input.priceHint,
    stripe_price_id: paid ? input.stripePriceId : null,
    stripe_price_id_test: paid ? input.stripePriceIdTest : null,
    custom_amount_min: paid ? input.customAmountMin : null,
    custom_amount_default: paid ? input.customAmountDefault : null,
    custom_amount_suffix: paid ? input.customAmountSuffix : null,
    is_free: input.isFree,
    featured: input.featured,
    active: input.active,
    sort_order: input.sortOrder,
    monthly_call_limit: input.monthlyCallLimit,
  };
}

export async function createMembershipTier(input: MembershipTierInput): Promise<void> {
  const { error } = await supabase.from('membership_tiers').insert(toTierPayload(input));
  if (error) throw new Error(error.message);
}

export async function updateMembershipTier(
  id: string,
  input: MembershipTierInput,
): Promise<void> {
  const { error } = await supabase
    .from('membership_tiers')
    .update(toTierPayload(input))
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function createMembershipFeature(input: {
  slug: string;
  label: string;
  sortOrder: number;
  active: boolean;
}): Promise<void> {
  const { error } = await supabase.from('membership_features').insert({
    slug: input.slug,
    label: input.label,
    sort_order: input.sortOrder,
    active: input.active,
  });
  if (error) throw new Error(error.message);
}

export async function updateMembershipFeature(
  id: string,
  input: { label: string; sortOrder: number; active: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('membership_features')
    .update({ label: input.label, sort_order: input.sortOrder, active: input.active })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/** 'hidden' deletes the join row; the other two upsert it. */
export async function setTierFeatureState(
  tierId: string,
  featureId: string,
  state: TierFeatureState,
): Promise<void> {
  if (state === 'hidden') {
    const { error } = await supabase
      .from('membership_tier_features')
      .delete()
      .eq('tier_id', tierId)
      .eq('feature_id', featureId);
    if (error) throw new Error(error.message);
    return;
  }

  const { error } = await supabase
    .from('membership_tier_features')
    .upsert(
      { tier_id: tierId, feature_id: featureId, included: state === 'included' },
      { onConflict: 'tier_id,feature_id' },
    );
  if (error) throw new Error(error.message);
}

export function nextTierFeatureState(state: TierFeatureState): TierFeatureState {
  if (state === 'hidden') return 'included';
  if (state === 'included') return 'excluded';
  return 'hidden';
}

/**
 * What the tier is missing before it can take real money. Surfaced in the list so a
 * tier that looks configured but cannot reach Stripe is visible at a glance.
 *
 * A test price alone is a distinct state, not an equivalent one: /upgrade works in the
 * sandbox and fails in production, which is exactly the thing worth a badge.
 */
export function tierReadinessIssue(tier: MembershipTierRow): string | null {
  if (tier.isFree) return null;
  if (tier.stripePriceId) return null;
  return tier.stripePriceIdTest ? 'Test price only' : 'No Stripe price';
}
